/**
 * The flow map as a plain table: every node in reading order with its kind,
 * where it comes from, where it leads, and its checklist and link counts.
 *
 * The server renders this next to the picture (the static widget toggles the
 * two with CSS only), so it is also what screen readers and crawlers read.
 * No handlers and no "use client". The interactive table reuses
 * `FlowTableRowCells` for its cells and adds sorting and selection around them.
 */
import type { ReactElement } from "react";
import { KindIcon } from "./icons.js";
import { FLOW_KIND_LABEL } from "./labels.js";
import type { FlowMapDoc, FlowMapLabels } from "./model.js";
import { FLOW_TABLE_COLUMNS, flowTableRows, kindTone, type FlowTableRef, type FlowTableRow } from "./table.js";

export interface FlowMapTableStaticProps {
  doc: FlowMapDoc;
  locale?: string;
  labels: FlowMapLabels;
  /** Accessible table caption (visually hidden). Default: the table view's name. */
  caption?: string;
}

function Refs({ refs, labels }: { refs: FlowTableRef[]; labels: FlowMapLabels }): ReactElement | null {
  if (!refs.length) return null;
  return (
    <div className="wa-fm-table__refs">
      {refs.map((r) => {
        // One text node (not two), so the server markup and the client tree match exactly.
        const text = `${r.title.trim() ? r.title : labels.untitled}${r.label ? ` · ${r.label}` : ""}`;
        return (
          <span key={r.id} className="wa-fm-chip">
            <span className="wa-fm-chip__text">{text}</span>
          </span>
        );
      })}
    </div>
  );
}

/** The seven cells of one row, in FLOW_TABLE_COLUMNS order. Pure markup, shared by both tables. */
export function FlowTableRowCells({ row, labels }: { row: FlowTableRow; labels: FlowMapLabels }): ReactElement {
  const hasTitle = row.title.trim().length > 0;
  return (
    <>
      <td className="wa-fm-table__num">{row.order}</td>
      <td>
        <div className={hasTitle ? "wa-fm-table__title" : "wa-fm-table__title is-empty"}>{hasTitle ? row.title : labels.untitled}</div>
        {row.description.trim() ? <div className="wa-fm-table__desc">{row.description}</div> : null}
        {row.groupId ? (
          <div className="wa-fm-table__group">
            <span className="wa-fm-chip">
              <KindIcon kind="group" size={12} />
              <span className="wa-fm-chip__text">{row.group.trim() ? row.group : labels.kindGroup}</span>
            </span>
          </div>
        ) : null}
      </td>
      <td>
        <span className={`wa-fm-chip wa-fm-chip--tone-${kindTone(row.kind, row.tone)}`}>
          <KindIcon kind={row.kind} size={12} />
          <span className="wa-fm-chip__text">{labels[FLOW_KIND_LABEL[row.kind]]}</span>
        </span>
      </td>
      <td>
        <Refs refs={row.from} labels={labels} />
      </td>
      <td>
        <Refs refs={row.to} labels={labels} />
      </td>
      <td className="wa-fm-table__num">{row.stepsTotal > 0 ? labels.stepsProgress(row.stepsDone, row.stepsTotal) : null}</td>
      <td className="wa-fm-table__num">{row.links > 0 ? row.links : null}</td>
    </>
  );
}

export function FlowMapTableStatic({ doc, locale, labels, caption }: FlowMapTableStaticProps): ReactElement {
  const rows = flowTableRows(doc, locale);
  return (
    <div className="wa-fm-table-wrap">
      <table className="wa-fm-table">
        <caption className="wa-fm__sr">{caption?.trim() ? caption : labels.tableView}</caption>
        <thead>
          <tr>
            {FLOW_TABLE_COLUMNS.map((c) => (
              <th key={c.key} scope="col" className={c.key === "order" || c.key === "steps" || c.key === "links" ? "wa-fm-table__num" : undefined}>
                {labels[c.label]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="wa-fm-table__empty" colSpan={FLOW_TABLE_COLUMNS.length}>
                {labels.emptyReadOnly}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id} data-node={row.id}>
                <FlowTableRowCells row={row} labels={labels} />
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
